import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell
} from "recharts";
import { 
  Building2, TrendingUp, TrendingDown, Wallet, ArrowUpRight, ArrowDownRight, 
  Upload, FileText, AlertCircle, Settings, LogOut, ChevronDown, Menu, X,
  LayoutDashboard, List, ClipboardCheck, FileBarChart, CreditCard, Shield, ArrowLeftRight, Calendar
} from "lucide-react";
import { toast } from "sonner";

const COLORS = ['#10B981', '#EF4444', '#F59E0B', '#6366F1', '#8B5CF6', '#EC4899', '#14B8A6', '#3B82F6'];

export default function DashboardPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [byAccount, setByAccount] = useState([]);
  const [byCategory, setByCategory] = useState([]);
  const [monthlyTrend, setMonthlyTrend] = useState([]);
  const [topExpenses, setTopExpenses] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("all");
  const [reviewCount, setReviewCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchDashboardData();
  }, [selectedAccount]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const accountParam = selectedAccount !== "all" ? `?account_id=${selectedAccount}` : "";
      
      const [summaryRes, accountsRes, categoryRes, trendRes, expensesRes, reviewRes, bankAccountsRes] = await Promise.all([
        axios.get(`${API}/dashboard/summary${accountParam}`, { headers, withCredentials: true }),
        axios.get(`${API}/dashboard/by-account`, { headers, withCredentials: true }),
        axios.get(`${API}/dashboard/by-category${accountParam}`, { headers, withCredentials: true }),
        axios.get(`${API}/dashboard/monthly-trend`, { headers, withCredentials: true }),
        axios.get(`${API}/dashboard/top-expenses?limit=10`, { headers, withCredentials: true }),
        axios.get(`${API}/transactions/review/queue?limit=1`, { headers, withCredentials: true }),
        axios.get(`${API}/accounts`, { headers, withCredentials: true })
      ]);
      
      setSummary(summaryRes.data);
      setByAccount(accountsRes.data);
      setByCategory(categoryRes.data);
      setMonthlyTrend(trendRes.data);
      setTopExpenses(expensesRes.data);
      setReviewCount(reviewRes.data.length);
      setAccounts(bankAccountsRes.data);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const NavLink = ({ to, icon: Icon, children, badge }) => (
    <Link
      to={to}
      className="flex items-center gap-3 px-3 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800/50 transition-colors"
    >
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
      {badge > 0 && (
        <span className="ml-auto bg-[#FCD34D] text-[#1C1917] text-xs font-bold px-2 py-0.5 rounded-full">
          {badge}
        </span>
      )}
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar - Desktop */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload}>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List}>Transactions</NavLink>
          <NavLink to="/transfers" icon={ArrowLeftRight}>Transfer Detection</NavLink>
          <NavLink to="/reconciliation" icon={Calendar}>Weekly Summary</NavLink>
          <NavLink to="/review" icon={ClipboardCheck} badge={reviewCount}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard}>Bank Accounts</NavLink>
          {user?.role === 'admin' && (
            <NavLink to="/admin" icon={Shield}>Admin Settings</NavLink>
          )}
        </nav>
        
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">
              {user?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800"
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-zinc-400"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="absolute top-16 left-0 right-0 bg-[#121214] border-b border-zinc-800 p-4 space-y-1">
            <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
            <NavLink to="/upload" icon={Upload}>Upload</NavLink>
            <NavLink to="/transactions" icon={List}>Transactions</NavLink>
            <NavLink to="/review" icon={ClipboardCheck} badge={reviewCount}>Review</NavLink>
            <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
            <NavLink to="/accounts" icon={CreditCard}>Accounts</NavLink>
            {user?.role === 'admin' && <NavLink to="/admin" icon={Shield}>Admin</NavLink>}
            <Button
              variant="ghost"
              className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800 mt-4"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </nav>
        )}
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Financial Dashboard</h1>
              <p className="text-zinc-400 text-sm mt-1">Consolidated view across all accounts</p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-800 text-white" data-testid="account-filter">
                  <SelectValue placeholder="All Accounts" />
                </SelectTrigger>
                <SelectContent className="bg-[#121214] border-zinc-800">
                  <SelectItem value="all" className="text-white hover:bg-zinc-800">All Accounts</SelectItem>
                  {accounts.map(acc => (
                    <SelectItem key={acc.account_id} value={acc.account_id} className="text-white hover:bg-zinc-800">
                      {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Link to="/upload">
                <Button className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]" data-testid="upload-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </Link>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-96">
              <div className="w-8 h-8 border-2 border-[#FCD34D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card className="bg-[#121214] border-zinc-800" data-testid="total-income-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-zinc-400 text-sm">Total Income</span>
                      <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-emerald-500" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold font-mono text-emerald-500">
                      {formatCurrency(summary?.total_income)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-[#121214] border-zinc-800" data-testid="direct-expenses-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-zinc-400 text-sm">Direct Expenses</span>
                      <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                        <ArrowDownRight className="w-5 h-5 text-red-500" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold font-mono text-red-500">
                      {formatCurrency(summary?.total_direct_expenses)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-[#121214] border-zinc-800" data-testid="indirect-expenses-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-zinc-400 text-sm">Indirect Expenses</span>
                      <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                        <TrendingDown className="w-5 h-5 text-orange-500" />
                      </div>
                    </div>
                    <p className="text-2xl font-bold font-mono text-orange-500">
                      {formatCurrency(summary?.total_indirect_expenses)}
                    </p>
                  </CardContent>
                </Card>

                <Card className="bg-[#121214] border-zinc-800 border-[#FCD34D]/20" data-testid="net-surplus-card">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-zinc-400 text-sm">Net Surplus</span>
                      <div className="w-10 h-10 rounded-lg bg-[#FCD34D]/10 flex items-center justify-center">
                        <Wallet className="w-5 h-5 text-[#FCD34D]" />
                      </div>
                    </div>
                    <p className={`text-2xl font-bold font-mono ${summary?.net_surplus >= 0 ? 'text-[#FCD34D]' : 'text-red-500'}`}>
                      {formatCurrency(summary?.net_surplus)}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid lg:grid-cols-2 gap-6 mb-8">
                {/* Monthly Trend */}
                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Monthly Trend</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={monthlyTrend}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                          <XAxis dataKey="month" stroke="#71717A" tick={{ fill: '#71717A', fontSize: 12 }} />
                          <YAxis stroke="#71717A" tick={{ fill: '#71717A', fontSize: 12 }} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#121214', border: '1px solid #27272A', borderRadius: '8px' }}
                            labelStyle={{ color: '#FAFAFA' }}
                          />
                          <Line type="monotone" dataKey="income" stroke="#10B981" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="direct_expenses" stroke="#EF4444" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="indirect_expenses" stroke="#F59E0B" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex items-center justify-center gap-6 mt-4">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-emerald-500" />
                        <span className="text-xs text-zinc-400">Income</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                        <span className="text-xs text-zinc-400">Direct</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full bg-orange-500" />
                        <span className="text-xs text-zinc-400">Indirect</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Category Breakdown */}
                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-lg text-white">Expense Breakdown</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-72">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={byCategory.filter(c => c.total_expenses > 0).slice(0, 8)}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="total_expenses"
                            nameKey="category"
                          >
                            {byCategory.filter(c => c.total_expenses > 0).slice(0, 8).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#121214', border: '1px solid #27272A', borderRadius: '8px' }}
                            formatter={(value) => formatCurrency(value)}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-wrap justify-center gap-3 mt-4">
                      {byCategory.filter(c => c.total_expenses > 0).slice(0, 6).map((cat, index) => (
                        <div key={cat.category} className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                          <span className="text-xs text-zinc-400">{cat.category}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Account Breakdown & Top Expenses */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* By Account */}
                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg text-white">By Account</CardTitle>
                    <Link to="/accounts" className="text-sm text-[#FCD34D] hover:underline">Manage</Link>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {byAccount.map((acc) => (
                        <div key={acc.account_id} className="p-4 bg-zinc-900/50 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-white">{acc.account_name}</span>
                            <span className={`font-mono text-sm ${acc.net_position >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                              {formatCurrency(acc.net_position)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-zinc-400">
                            <span>In: <span className="text-emerald-500">{formatCurrency(acc.total_income)}</span></span>
                            <span>Out: <span className="text-red-500">{formatCurrency(acc.total_expenses)}</span></span>
                            <span>{acc.transaction_count} txns</span>
                          </div>
                        </div>
                      ))}
                      {byAccount.length === 0 && (
                        <p className="text-center text-zinc-500 py-8">No transaction data yet. Upload statements to get started.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Expenses */}
                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg text-white">Largest Expenses</CardTitle>
                    <Link to="/transactions" className="text-sm text-[#FCD34D] hover:underline">View All</Link>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {topExpenses.slice(0, 8).map((tx, index) => (
                        <div key={tx.transaction_id} className="flex items-center justify-between py-2 border-b border-zinc-800 last:border-0">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{tx.description}</p>
                            <p className="text-xs text-zinc-500">{tx.category}</p>
                          </div>
                          <span className="font-mono text-sm text-red-500 ml-4">
                            -{formatCurrency(tx.amount_out)}
                          </span>
                        </div>
                      ))}
                      {topExpenses.length === 0 && (
                        <p className="text-center text-zinc-500 py-8">No expenses recorded yet.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
